import type {
  BacklogListResolveRow,
  BacklogListSourceSystem,
  BacklogListImportOptions,
  BacklogDurationStrategy,
} from "../../../../../../../../../../../../../../../api/kehrblatt/backlog-lists"
import { summedTypicalDurationMinutes } from "../../../../../../../../../../../../../_shared/_logic/order-type-selection"
import { MAX_PREPARATION_DURATION_MINUTES } from "../../../../../_shared/_logic/work-order-creation"

export type PricePart = {
  id: string
  column: string
  value: string
  amountCents: number | null
  disposition: string
}
export type BacklogEstimate = {
  priceCents: number | null
  totalPrice: boolean
  durationMinutes: number | null
  explanation: string
  parts: PricePart[]
  warning: string | null
  method: "price" | "typical" | "manual"
}

export const formatEuroCents = (cents: number): string =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100)

/** Convert form input exactly once, before it becomes an integer wire value. */
export const euroInputCents = (value: unknown): number | null => {
  const match = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(String(value ?? "").trim())

  if (!match) return null

  const cents = BigInt(match[1]) * 100n + BigInt((match[2] ?? "").padEnd(2, "0"))

  return cents <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(cents) : null
}

export const durationStrategy = (options?: BacklogListImportOptions): BacklogDurationStrategy =>
  options?.contract_version === 2 ? options.duration_strategy : { method: "typical" }

/** One selection-dependent calculation for the draft and its visible explanation. */
export const estimateBacklogRow = ({
  sources,
  sourceSystem,
  sourceColumns,
  selectedIds,
  customType,
  orderTypes,
  options,
}: {
  sources: BacklogListResolveRow[]
  sourceSystem?: BacklogListSourceSystem
  sourceColumns: string[]
  selectedIds: readonly string[]
  customType?: unknown
  orderTypes: readonly OrderType[]
  options?: BacklogListImportOptions
}): BacklogEstimate => {
  const selected = [...new Set(selectedIds)]
  const byId = new Map(orderTypes.map((type) => [type.id, type]))
  const types = selected.map((id) => byId.get(id))
  const typicalComplete =
    !customType &&
    selected.length > 0 &&
    types.every(
      (type) =>
        type && Number.isFinite(type.typical_duration_min) && Number(type.typical_duration_min) > 0,
    )
  const typical = typicalComplete ? summedTypicalDurationMinutes(selected, orderTypes) : null
  const selectedKeys = new Set(
    types.flatMap((type) => (type?.is_protected && type.key ? [type.key] : [])),
  )
  const strategy = durationStrategy(options)
  const result: BacklogEstimate = {
    priceCents: null,
    totalPrice: sourceSystem === "portal_uno_office",
    durationMinutes: typical,
    explanation: "",
    parts: [],
    warning: null,
    method: "typical",
  }
  const addTypicalExplanation = (reason: string) => {
    const detail = types
      .filter((type) => type && Number(type.typical_duration_min) > 0)
      .map((type) => `${type!.internal_label}: ${type!.typical_duration_min} Min.`)
      .join(" + ")
    result.explanation =
      reason +
      (typical
        ? ` Typische Dauer: ${detail} = ${typical} Minuten.`
        : " Bitte eine Dauer eintragen; nicht für alle ausgewählten Arbeiten ist eine typische Dauer hinterlegt.")
  }
  const identities = new Set(sources.map((row) => row.source_identity ?? row.source_row_id))
  let eligible = sources.length > 0 && identities.size === 1 && selected.length > 0 && !customType
  const components = sources.flatMap((row) => row.price_components ?? [])
  const reconsideredBySlot = new Map(
    sources.flatMap((row) =>
      (row.work_evidence ?? []).flatMap((work) =>
        sourceSystem === "portal_kamin_futura" &&
        work.status === "open" &&
        work.reconsidered_from_date
          ? [[`${row.source_row_id}:${work.slot}`, work] as const]
          : [],
      ),
    ),
  )
  const reconsideredReasons = new Set<string>()
  let reason = sources.length
    ? "Für diese Auswahl ist kein vollständiger Listenpreis bekannt."
    : "Dieser Zeile ist kein eigener Listenpreis zugeordnet."
  const known = new Set<string>()
  const representedKeys = new Set<string>()
  let cents = 0n
  let hasPrice = false
  let incomplete = false
  for (const part of components) {
    const isTotal = part.slot === "uno_total"
    const included =
      isTotal || Boolean(part.order_type_key && selectedKeys.has(part.order_type_key))
    const reconsidered = reconsideredBySlot.get(`${part.source_row_id}:${part.slot}`)
    const fingerprint = `${part.slot}:${part.status}:${part.amount_cents ?? part.value}`
    let disposition = included ? "berücksichtigt" : "abgewählt"

    if (part.status === "done") disposition = "erledigt"
    else if (part.status === "empty") disposition = "leer"
    else if (part.status === "duplicate" || known.has(fingerprint)) disposition = "doppelt"
    else if (part.status === "conflict") disposition = "widersprüchlich"
    else if (part.status === "unknown")
      disposition = included
        ? reconsidered
          ? "Datum älter als zwölf Monate; berücksichtigt"
          : "Preis unklar"
        : "abgewählt"
    else if (!part.order_type_key && !isTotal) disposition = "Sonderarbeit: nicht zugeordnet"

    result.parts.push({
      id: `${part.source_row_id}:${part.column_index}`,
      column: sourceColumns[part.column_index] || part.slot,
      value: part.value,
      amountCents: part.amount_cents,
      disposition,
    })

    if (!included || ["done", "empty", "duplicate"].includes(part.status) || known.has(fingerprint))
      continue

    known.add(fingerprint)

    if (reconsidered && part.status === "unknown") {
      const date = reconsidered.reconsidered_from_date!.split("-").reverse().join(".")
      reconsideredReasons.add(
        `${reconsidered.column}: Datum ${date} liegt mehr als zwölf Monate zurück. Die Arbeit wird berücksichtigt.`,
      )
    }

    if (part.order_type_key) representedKeys.add(part.order_type_key)

    if (
      part.status !== "priced" ||
      !Number.isSafeInteger(part.amount_cents) ||
      part.amount_cents! < 0
    ) {
      incomplete = true

      if (part.status === "conflict")
        result.warning = "Widersprüchliche Preisangaben. Bitte die Quelle prüfen."
    } else {
      cents += BigInt(part.amount_cents!)
      hasPrice = true
    }
  }

  if (sourceSystem === "portal_uno_office") {
    const evidence = sources.flatMap((row) => row.work_evidence ?? [])
    const offered = new Set(
      evidence.flatMap((item) =>
        item.status === "open" && item.order_type_key ? [item.order_type_key] : [],
      ),
    )
    const completeSelection =
      offered.size > 0 &&
      offered.size === selectedKeys.size &&
      [...offered].every((key) => selectedKeys.has(key)) &&
      selectedKeys.size === selected.length
    const clearWork =
      evidence.length > 0 && evidence.every((item) => item.status === "open" && item.order_type_key)
    eligible &&= completeSelection && clearWork && !sources.some((row) => row.work_warnings?.length)

    if (!completeSelection)
      reason =
        "UNO enthält nur einen Gesamtpreis. Für Ihre geänderte Arbeitsauswahl verwenden wir die typischen Dauern."
    else if (!clearWork)
      reason = "Die Arbeitsangaben zum UNO-Gesamtpreis sind nicht vollständig eindeutig."

    if (
      hasPrice &&
      !incomplete &&
      identities.size === 1 &&
      cents <= BigInt(Number.MAX_SAFE_INTEGER)
    )
      result.priceCents = Number(cents)
  } else if (sourceSystem === "portal_kamin_futura") {
    const covered =
      selectedKeys.size === selected.length &&
      [...selectedKeys].every((key) => representedKeys.has(key))
    eligible &&= covered

    if (
      hasPrice &&
      !incomplete &&
      covered &&
      !customType &&
      identities.size === 1 &&
      cents <= BigInt(Number.MAX_SAFE_INTEGER)
    )
      result.priceCents = Number(cents)
  } else eligible = false

  if (identities.size > 1) {
    eligible = false
    result.warning = "Mehrere nicht eindeutig zusammengehörige Quellzeilen. Bitte den Preis prüfen."
    reason = result.warning
  }

  if (reconsideredReasons.size && !result.warning) {
    reason = `${[...reconsideredReasons].join(" ")} Für diese Zeile verwenden wir die typischen Dauern.`
  }

  const fixedMinutes =
    strategy.method === "fixed"
      ? strategy.duration_minutes
      : options?.contract_version === 1 && options.mode === "manual"
        ? options.duration_minutes
        : null

  if (fixedMinutes !== null) {
    const minutes = fixedMinutes
    result.method = "manual"
    result.durationMinutes =
      Number.isInteger(minutes) && minutes >= 5 && minutes <= MAX_PREPARATION_DURATION_MINUTES
        ? minutes
        : null
    result.explanation = `${minutes} Minuten beim Import gemeinsam vorgegeben.`

    if (result.durationMinutes === null) {
      result.warning = `Die Vorgabe liegt außerhalb von 5 bis ${MAX_PREPARATION_DURATION_MINUTES} Minuten. Bitte eine passende Dauer eintragen.`
    }

    return result
  }

  if (strategy.method === "price" && eligible && result.priceCents !== null && !incomplete) {
    const price = result.priceCents

    if (
      price > 0 &&
      price >= strategy.minimum_price_cents &&
      Number.isSafeInteger(strategy.hourly_rate_cents) &&
      strategy.hourly_rate_cents > 0
    ) {
      const rate = BigInt(strategy.hourly_rate_cents)
      const minutes = ((BigInt(price) * 12n + rate - 1n) / rate) * 5n
      result.method = "price"
      result.durationMinutes =
        minutes <= BigInt(MAX_PREPARATION_DURATION_MINUTES) ? Number(minutes) : null
      const rawMinutes = ((price / strategy.hourly_rate_cents) * 60).toLocaleString("de-DE", {
        maximumFractionDigits: 2,
      })
      result.explanation = `${formatEuroCents(price)} ÷ ${formatEuroCents(strategy.hourly_rate_cents)}/Std. × 60 = ${rawMinutes} Minuten → aufgerundet ${minutes} Minuten.`

      if (minutes > BigInt(MAX_PREPARATION_DURATION_MINUTES)) {
        result.warning = `Die berechnete Dauer überschreitet ${MAX_PREPARATION_DURATION_MINUTES} Minuten. Bitte eine passende Dauer eintragen.`
        result.explanation += " Bitte die Dauer manuell prüfen."
      }

      return result
    }

    reason =
      price === 0
        ? "Der Listenpreis beträgt 0,00 €. Deshalb gilt die typische Dauer."
        : `${formatEuroCents(price)} liegen unter Ihrer Grenze von ${formatEuroCents(strategy.minimum_price_cents)}. Deshalb gilt die typische Dauer.`
  }

  if (typical !== null && (typical < 5 || typical > MAX_PREPARATION_DURATION_MINUTES)) {
    result.durationMinutes = null
    result.warning = `Die typische Dauer liegt außerhalb von 5 bis ${MAX_PREPARATION_DURATION_MINUTES} Minuten. Bitte eine passende Dauer eintragen.`
  }

  addTypicalExplanation(
    strategy.method === "typical" && !reconsideredReasons.size
      ? "Die typischen Dauern der ausgewählten Auftragstypen werden verwendet."
      : reason,
  )

  if (result.durationMinutes === null && typical !== null)
    result.explanation += " Bitte die Dauer manuell prüfen."

  return result
}
